import { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createArtwork, getArtworkById, getArtworks, reset, updateArtwork } from '../../features/artwork/artworkSlice';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { FaTimes } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import Loading from '../../components/Loading';
import 'react-quill/dist/quill.snow.css';

function AddArtwork() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isLoading, artwork, artworks } = useSelector((state) => state.artwork);

  // Refs for file inputs
  const imageInputRef = useRef(null);

  // Core form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('active');
  const [sortOrder, setSortOrder] = useState(99);
  const [image, setImage] = useState(null);
  const [usedForPrints, setUsedForPrints] = useState(false);

  // Image preview state
  const [imagePreview, setImagePreview] = useState(null);

  // --- Effect to fetch artwork data on load (for editing) ---
  useEffect(() => {
    dispatch(getArtworks());
    if (id) {
        dispatch(getArtworkById(id));
    }
    
    return () => {
        dispatch(reset());
    };
  }, [id, dispatch]);

  // --- Effect to populate form when artwork data is loaded ---
  useEffect(() => {
    if (artwork && id) {
        setTitle(artwork.title || '');
        setDescription(artwork.description || '');
        setCategory(artwork.category || '');
        setStatus(artwork.status || 'active');
        setSortOrder(artwork.sort_order || 99);
        setUsedForPrints(Boolean(artwork.used_for_prints));

        // Handle image - fix the image handling here
        if (artwork.image) {
            const originalImage = typeof artwork.image === 'object' ? (artwork.image.original?.src || artwork.image.src) : artwork.image;
            const previewImage = typeof artwork.image === 'object' ? artwork.image.src : artwork.image;
            setImage(originalImage);
            setImagePreview(previewImage);
        }
    }
}, [artwork, id]);

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(artworks)) return [];
    const unique = new Set();
    artworks.forEach((item) => {
      if (item?.category) unique.add(item.category);
    });
    return Array.from(unique).sort();
  }, [artworks]);

  const categoryWrapperRef = useRef(null);

  // 2. Add State to toggle the dropdown visibility
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // 3. Add Filter Logic (filters options based on what you type)
  const filteredCategories = useMemo(() => {
    if (!category) return categoryOptions;
    return categoryOptions.filter(opt => 
        opt.toLowerCase().includes(category.toLowerCase())
    );
  }, [category, categoryOptions]);

  // 4. Add Effect to close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoryWrapperRef.current && !categoryWrapperRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Event Handlers ---
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
        setImage(file);
        setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const artworkData = {
      title, 
      description, 
      category, 
      status,
      sort_order: Number(sortOrder),
      image,
      used_for_prints: usedForPrints,
    };
    
    // Dispatch create or update based on whether we have an id
    if (id) {
        dispatch(updateArtwork({ id, artworkData }));
    } else {
        dispatch(createArtwork(artworkData));
    }
  };

  // --- React Quill Configuration ---
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['clean']
    ],
  };

  if (isLoading) {
    return <Loading />; 
  }

  // --- Render ---
  return (
    <div className="admin-container">
      <div className="admin-header-container">
        <h1 className="admin-header">
          {id ? 'Edit Artwork' : 'Create New Artwork'}
        </h1>
        <button
          type="button"
          className="btn btn-secondary btn-close"
          onClick={() => navigate('/admin/artworks')}
        >
          <FaTimes size={16} />
          <span>Close</span>
        </button>
     </div>
      
      <form onSubmit={handleSubmit}>
        <div className="form-section">
            <div className="form-group">
                <label>Title:</label>
                <input 
                    type="text" 
                    className="form-input"
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)} 
                    required 
                />
            </div>
            <div className="form-group">
                <label>Description:</label>
                <ReactQuill 
                    theme="snow" 
                    value={description} 
                    onChange={setDescription}
                    modules={quillModules}
                    className="quill-editor"
                />
            </div>
            <div className="form-group" ref={categoryWrapperRef}>
              <label>Category:</label>
              <div className="custom-dropdown-wrapper">
                  <input 
                      type="text" 
                      className="form-input"
                      value={category} 
                      onChange={(e) => {
                          setCategory(e.target.value);
                          setShowCategoryDropdown(true);
                      }} 
                      onFocus={() => setShowCategoryDropdown(true)}
                      placeholder="Choose or type a category"
                      autoComplete="off" /* Important: stops browser history from covering your menu */
                      required 
                  />
                  
                  {/* Render the dropdown only if open and has options */}
                  {showCategoryDropdown && filteredCategories.length > 0 && (
                      <ul className="dropdown-options">
                          {filteredCategories.map((option, index) => (
                              <li 
                                  key={index} 
                                  className="dropdown-item"
                                  onClick={() => {
                                      setCategory(option);
                                      setShowCategoryDropdown(false);
                                  }}
                              >
                                  {option}
                              </li>
                          ))}
                      </ul>
                  )}
              </div>
            </div>
            <div className="form-group form-checkbox">
                <label className="form-checkbox-inline">
                    <input
                        type="checkbox"
                        checked={usedForPrints}
                        onChange={(e) => setUsedForPrints(e.target.checked)}
                    />
                    <span style={{ marginLeft: 8 }}>Use this artwork for prints</span>
                </label>
                <small>Checked artworks automatically get all print size variants under the dedicated prints product.</small>
            </div>

            <div className="form-group">
                <label>Status:</label>
                <select 
                    className="form-select" 
                    value={status} 
                    onChange={(e) => setStatus(e.target.value)}
                >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                </select>
            </div>
            <div className="form-group">
                <label>Sort Order:</label>
                <input 
                    type="number" 
                    className="form-input"
                    value={sortOrder} 
                    onChange={(e) => setSortOrder(e.target.value)} 
                />
            </div>

            {/* Image Uploader */}
            <div className="form-group">
                <label>Image:</label>
                <input 
                    type="file" 
                    id="image-input" 
                    ref={imageInputRef}
                    style={{ display: 'none' }}
                    onChange={handleImageChange}
                    required={!id} // Only required for new artworks
                />
                <div 
                    className="image-uploader" 
                    onClick={() => imageInputRef.current.click()}
                >
                    {imagePreview ? (
                        <img src={imagePreview} alt="Artwork preview" />
                    ) : (
                        <span>Click to upload artwork image</span>
                    )}
                </div>
            </div>
        </div>
        
        <button type="submit" className="btn btn-primary" disabled={isLoading} style={{ width: '100%', marginTop: '20px', padding: '12px', fontSize: '1.1em' }}>
          {isLoading ? 'Saving...' : (id ? 'Update Artwork' : 'Create Artwork')}
        </button>
      </form>
    </div>
  );
}

export default AddArtwork;
